import React from 'react';
import { CopyPanel } from './CopyPanel';
import { getSelectionData } from 'util/cite/getSelectionData';
import { LINE_SEP } from 'util/cite/';
import { getCitation } from 'util/getCitation';
import { finder } from 'util/finder';
import * as clipboard from 'clipboard-polyfill';
import { LayoutContext } from 'components/layouts/LayoutContext';

export const CitableDocument = ({
  className,
  children,
  contentRef,
  ...props
}) => {
  const citableRef = React.useRef();
  const [showCopy, setShowCopy] = React.useState(false);
  const [copyOffset, setCopyOffset] = React.useState(0);
  const [selection, setSelection] = React.useState();

  const { raw } = React.useContext(LayoutContext);

  const onContentSelect = React.useCallback(e => {
    if (raw) return;
    const sel = document.getSelection();
    setSelection(sel);
    if (!sel.anchorNode) return;
    const reverse =
      sel.focusNode.compareDocumentPosition(sel.anchorNode) &
      Node.DOCUMENT_POSITION_FOLLOWING;

    let focusNode =
      !reverse && sel.focusNode.nodeType !== 3 && sel.focusOffset === 0
        ? sel.focusNode.previousSibling
        : sel.focusNode.parentNode;

    if (focusNode === citableRef.current) {
      sel.modify('extend', 'backward', 'character');
    }

    if (
      isDescendant(citableRef.current, sel.anchorNode) &&
      isDescendant(citableRef.current, focusNode)
    ) {
      if (sel.toString()) {
        setCopyOffset(focusNode.offsetTop);
        setShowCopy(true);
      } else {
        setShowCopy(false);
      }
    } else {
      setShowCopy(false);
    }
  });

  const onCopyClick = React.useCallback((e, format) => {
    e.preventDefault();
    const selection = document.getSelection();
    const container = document.createElement('div');
    for (const el of Array(selection.rangeCount)
      .fill()
      .map((_, i) => selection.getRangeAt(i).cloneContents())) {
      container.appendChild(el);
    }
    finder(container)('mark').map(unwrapNode);
    const selectionData = getSelectionData();

    const citation = getCitation(
      props[`${format}CitationFormat`],
      format,
      selectionData,
      props.citeSearch,
      props.citeReplace,
    );

    const text = `${selection.toString()}\n\n${citation.text}`;
    const baseLink = document.createElement('a');
    baseLink.setAttribute('href', '/');

    const html = `${container.innerHTML.replace(
      /href=(['"])\//,
      `href=$1${baseLink.protocol}//${baseLink.host}/`,
    )}<br/><br/>${citation.html}`;
    // const rtf =
    //   '{\\rtf1\\ansi\\pard ' +
    //   escapeRTF(selection.toString()) +
    //   '\\line\\line' +
    //   citation.rtf +
    //   '}';

    var dt = new clipboard.DT();
    // Selection.toString will turn selected <br>'s into newline
    // characters, which are ignored in HTML. Turn any appended
    // newlines or selected <br>'s into <br>'s.

    dt.setData('text/html', html.replace(/\n/g, '<br />'));
    dt.setData('text/plain', text.replace(/\n/g, LINE_SEP));
    // dt.setData('text/rtf', rtf);
    clipboard.write(dt);
  });

  useSelectionChange(onContentSelect);

  const onMouseUp = React.useCallback(e => {
    setTimeout(() => {
      if (
        document.getSelection().type !== 'Range' &&
        citableRef?.current?.contains(e.target)
      ) {
        setShowCopy(false);
      }
    }, 0);
  });

  React.useLayoutEffect(() => {
    if (!raw) document.addEventListener('mouseup', onMouseUp);
    return () => {
      if (!raw) document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  return (
    <div className={className || 'citable-document'} ref={citableRef}>
      {showCopy ? (
        <CopyPanel
          top={copyOffset}
          onCopyClick={onCopyClick}
          selection={selection}
          selectableSelector={props.selectableSelector}
          unspannableSelector={props.unspannableSelector}
          unspannableParent={props.unspannableParent}
          containerSelector={`.${className || 'citable-document'}`}
        />
      ) : null}
      {children}
    </div>
  );
};

function useSelectionChange(onContentSelect) {
  React.useLayoutEffect(() => {
    document.addEventListener('selectionchange', onContentSelect);
    return () => {
      document.removeEventListener('selectionchange', onContentSelect);
    };
  }, []);
}

function isDescendant(parent, child) {
  if (!child) {
    return false;
  }
  var node = child.parentNode;
  while (node != null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}

const unwrapNode = function(node) {
  [...node.childNodes].map(content =>
    node.parentNode.insertBefore(content, node),
  );
  node.parentNode.removeChild(node);
  return node;
};
